import { Helmet } from 'react-helmet-async';

// SEO — per-route meta tag manager.
//
// Wraps react-helmet-async with our defaults + sensible OpenGraph / Twitter card
// boilerplate so individual pages just supply the few fields that differ from page
// to page (title, description, optional path).
//
// Usage at the top of each public page:
//   <SEO
//     title="Pricing"
//     description="Simple per-seat pricing. AUD $16/seat for Basic. AUD $29 for Enterprise."
//     path="/pricing"
//   />
//
// For auth pages + admin pages: pass noIndex prop so robots stay out.
//   <SEO title="Sign in" noIndex />
//
// Defaults:
//   - Title is suffixed with " — Giraops" so search-result titles look consistent
//   - OG image: /giraops-logo.png (Firebase Hosting serves it; Twitter + LinkedIn
//     will use it as the share preview)
//   - Canonical URL constructed from path

const BASE_URL = 'https://giraops.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/giraops-logo.png`;

export default function SEO({
  title,
  description,
  path = '',
  noIndex = false,
  ogImage = DEFAULT_OG_IMAGE,
  type = 'website',
  structuredData = null,
}) {
  const fullTitle = title ? `${title} — Giraops` : 'Giraops — 24/7 service hotline';
  const canonical = `${BASE_URL}${path}`;
  const desc = description || '24/7 service hotline for vendors who keep critical equipment running. Persistent escalation, customer-facing intake, built-in job chat.';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* OpenGraph (Facebook, LinkedIn, WhatsApp, Slack) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Giraops" />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data (only when supplied — e.g. SoftwareApplication on landing) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
